/**
 * Asset-context header for a checklist instance that references an asset.
 *
 * Shows — mirroring the Asset Panel — the asset photo (if any), DIN 276
 * category + name, asset name/id, building address (link to building detail)
 * and floor name (link to floor detail). Self-contained: resolves the asset,
 * its building and floor, and the DIN 276 name from the checklist's `asset`
 * id (re-attached server-side on checklist instances).
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ExternalLink } from "lucide-react";

import { Asset, Building, Floor, from, is } from "@the-real-insight/tri-model";
import { Button } from "@/components/ui/button";
import { userAPI } from "../UserAPI";

interface Properties {
  /** asset _id from the checklist instance */
  assetId?: string;
  /** fallback display name from the checklist instance */
  assetName?: string;
  /** floor _id from the checklist instance, if the asset itself has none */
  floorId?: string;
}

function formatCountryForBuildingId(country: unknown): string {
  if (country == null) {
    return "DE";
  }

  if (typeof country === "string") {
    const trimmed = country.trim();
    if (trimmed === "") {
      return "DE";
    }
    return trimmed.length === 2 ? trimmed.toUpperCase() : trimmed;
  }

  return String(country);
}

export default function ChecklistAssetContext(properties: Properties) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [asset, setAsset] = useState<any>(null);
  const [building, setBuilding] = useState<any>(null);
  const [floor, setFloor] = useState<any>(null);
  const [din276Name, setDin276Name] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setAsset(null);
      setBuilding(null);
      setFloor(null);
      setDin276Name(null);

      if (!properties.assetId) {
        return;
      }
      try {
        const loadedAsset = await userAPI.queryOne(
          from(Asset.type).where(is("_id").equal(properties.assetId)),
        );
        if (cancelled || !loadedAsset) {
          return;
        }
        setAsset(loadedAsset);

        // DIN 276 category name
        if ((loadedAsset as any).schemaCategory) {
          try {
            const metadata: any[] = await userAPI.getDIN276Metadata();
            const match = (metadata || []).find(
              (entry: any) => String(entry.code) === String((loadedAsset as any).schemaCategory),
            );
            if (!cancelled && match) setDin276Name(match.name || null);
          } catch {
            /* DIN metadata optional */
          }
        }

        // Building by composite id (same key the Asset Panel uses)
        const a = loadedAsset as any;
        if (a.street && a.city) {
          try {
            const buildings = await userAPI.query(
              from(Building.type).where(
                is("id").equal(
                  `${a.street} ${a.streetNumber}, ${a.postalCode} ${a.city}, ${formatCountryForBuildingId(a.country)}`,
                ),
              ),
            );
            if (!cancelled && Array.isArray(buildings) && buildings.length === 1) {
              setBuilding(buildings[0]);
            }
          } catch {
            /* building optional */
          }
        }

        // Floor: prefer the asset's floor, fall back to the checklist's
        const floorId = a.floor || properties.floorId;
        if (floorId) {
          try {
            const loadedFloor = await userAPI.queryOne(
              from(Floor.type).where(is("_id").equal(floorId)),
            );
            if (!cancelled && loadedFloor) setFloor(loadedFloor);
          } catch {
            /* floor optional */
          }
        }
      } catch {
        /* asset resolution is best-effort; the checklist still renders */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [properties.assetId, properties.floorId]);

  if (!properties.assetId && !properties.assetName) {
    return null;
  }

  const a = asset || {};
  const photoPath = Array.isArray(a.photos) && a.photos.length > 0 ? a.photos[0] : a.image || null;
  const addressLine =
    a.street || a.city
      ? `${a.street || ""} ${a.streetNumber || ""}, ${a.postalCode || ""} ${a.city || ""}`.trim()
      : null;

  return (
    <div className="flex min-w-0 flex-wrap items-start gap-6 rounded-md border p-3">
      {photoPath ? (
        <div
          role="img"
          aria-label={a.name || properties.assetName}
          className="h-[120px] w-[120px] shrink-0 rounded-md border bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${userAPI.getResourceURL(photoPath)})` }}
        />
      ) : null}
      <div className="min-w-0 text-sm leading-relaxed">
        <div className="flex min-w-0 flex-wrap items-center gap-1">
          <span className="min-w-0 break-words font-bold">
            {a.id ? `${a.id} · ` : ""}
            {a.name || properties.assetName}
          </span>
          {properties.assetId ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              title={t("checklistAssetContext.openAsset")}
              aria-label={t("checklistAssetContext.openAsset")}
              onClick={() =>
                navigate(
                  `/entity?selectedEntityType=Asset&selectedEntity=${properties.assetId}`,
                )
              }
            >
              <ExternalLink aria-hidden="true" className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
        {a.schemaCategory ? (
          <div className="break-words text-muted-foreground">
            {t("checklistAssetContext.din276")}: {a.schemaCategory}
            {din276Name ? ` — ${din276Name}` : ""}
          </div>
        ) : null}
        {addressLine ? (
          <div className="flex min-w-0 flex-wrap items-center gap-1">
            <span className="min-w-0 break-words">{addressLine}</span>
            {building ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                title={t("checklistAssetContext.openBuilding")}
                aria-label={t("checklistAssetContext.openBuilding")}
                onClick={() =>
                  navigate(
                    `/entity?selectedEntityType=Building&selectedEntity=${building._id}`,
                  )
                }
              >
                <ExternalLink aria-hidden="true" className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        ) : null}
        {floor ? (
          <div className="flex min-w-0 flex-wrap items-center gap-1">
            <span className="min-w-0 break-words">
              {t("checklistAssetContext.floor")}: {floor.name}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              title={t("checklistAssetContext.openFloor")}
              aria-label={t("checklistAssetContext.openFloor")}
              onClick={() =>
                navigate(`/entity?selectedEntityType=Floor&selectedEntity=${floor._id}`)
              }
            >
              <ExternalLink aria-hidden="true" className="h-4 w-4" />
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
